import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Calendar, Phone, FileText, ChefHat, Lock } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch { return iso; }
};
const SECTION_META = {
  calendar:  { icon: Calendar, label: "Calendar", emoji: "📅" },
  contacts:  { icon: Phone,    label: "Contacts", emoji: "📞" },
  documents: { icon: FileText, label: "Documents", emoji: "📄" },
  recipes:   { icon: ChefHat,  label: "Recipes",  emoji: "🍳" },
};

export default function PublicTopic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/topic/${token}`);
        if (!res.ok) {
          const detail = (await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`;
          if (!cancelled) setError(detail);
        } else {
          const json = await res.json();
          if (!cancelled) setData(json);
        }
      } catch {
        if (!cancelled) setError("Could not load this link.");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--hh-cream)" }}>
      <div className="nb-card p-8"><p className="text-lg" style={{ fontFamily: "var(--hh-display)" }}>Loading…</p></div>
    </div>;
  }
  if (error) {
    return <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--hh-cream)" }}>
      <div className="nb-card max-w-md p-9 text-center" data-testid="topic-error">
        <Lock strokeWidth={2} className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--hh-terracotta)" }} />
        <h1 className="text-3xl font-extrabold mb-2" style={{ fontFamily: "var(--hh-display)" }}>Link unavailable</h1>
        <p className="text-sm" style={{ color: "var(--hh-ink-soft)" }}>{error}</p>
      </div>
    </div>;
  }

  const meta = SECTION_META[data.section] || { icon: Calendar, label: data.section, emoji: "📁" };
  const Icon = meta.icon;

  return (
    <div className="min-h-screen" style={{ background: "var(--hh-cream)" }} data-testid="public-topic-root">
      <header className="px-6 sm:px-12 py-10 max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full border text-[11px] font-semibold tracking-wide uppercase"
             style={{ background: "rgba(212,162,76,0.12)", borderColor: "var(--hh-line)", color: "var(--hh-ink-soft)" }}>
          <Icon strokeWidth={2.4} className="w-3 h-3" /> {meta.label} · shared with you
        </div>
        <h1 className="text-5xl sm:text-6xl font-extrabold leading-[0.95] tracking-tight" style={{ fontFamily: "var(--hh-display)" }}>
          The {data.family.name}'s {meta.label.toLowerCase()}
        </h1>
        {data.note && (
          <p className="mt-4 text-base max-w-xl italic" style={{ color: "var(--hh-ink-soft)" }}>
            "{data.note}"
          </p>
        )}
      </header>

      <main className="px-6 sm:px-12 max-w-4xl mx-auto pb-20">
        {data.items.length === 0 ? (
          <p className="text-center text-sm" style={{ color: "var(--hh-ink-soft)" }}>Nothing to show here yet.</p>
        ) : data.section === "calendar" ? (
          <ul className="space-y-3" data-testid="topic-items">
            {data.items.map((e) => (
              <li key={e.id} className="nb-card !p-5 flex items-center gap-4">
                <span style={{ background: "var(--hh-honey)" }} className="w-1.5 h-12 rounded-full shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{e.title}</div>
                  {e.location && <div className="text-xs" style={{ color: "var(--hh-ink-soft)" }}>{e.location}</div>}
                </div>
                <div className="text-xs whitespace-nowrap" style={{ color: "var(--hh-ink-soft)" }}>{fmtDate(e.start)}</div>
              </li>
            ))}
          </ul>
        ) : data.section === "contacts" ? (
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="topic-items">
            {data.items.map((c) => (
              <li key={c.id} className="nb-card !p-4">
                <div className="font-semibold">{c.name}</div>
                {c.relationship && <div className="text-xs mb-1.5" style={{ color: "var(--hh-ink-soft)" }}>{c.relationship}</div>}
                {c.phone && <div className="text-sm">📞 <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a></div>}
                {c.email && <div className="text-sm">✉ <a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a></div>}
              </li>
            ))}
          </ul>
        ) : data.section === "documents" ? (
          <ul className="space-y-2" data-testid="topic-items">
            {data.items.map((d) => (
              <li key={d.id} className="nb-card !p-4 flex items-center gap-3">
                <FileText strokeWidth={2.2} className="w-5 h-5" style={{ color: "var(--hh-terracotta)" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{d.title}</div>
                  <div className="text-xs" style={{ color: "var(--hh-ink-soft)" }}>{d.category} · {((d.size || 0)/1024).toFixed(0)} KB</div>
                </div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="nb-btn px-3 py-1.5 text-xs">Open →</a>
              </li>
            ))}
          </ul>
        ) : data.section === "recipes" ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="topic-items">
            {data.items.map((r) => (
              <div key={r.id} className="nb-card !p-0 overflow-hidden">
                {r.image_url && <img src={r.image_url} alt={r.title} className="w-full h-32 object-cover" />}
                <div className="p-4">
                  <div className="font-extrabold text-lg" style={{ fontFamily: "var(--hh-display)" }}>{r.title}</div>
                  <div className="text-xs mb-2" style={{ color: "var(--hh-ink-soft)" }}>{r.prep_time}m · serves {r.servings}</div>
                  {r.description && <p className="text-sm line-clamp-2">{r.description}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </main>

      <footer className="px-6 sm:px-12 pb-10 text-center text-xs" style={{ color: "var(--hh-ink-soft)" }}>
        Shared via familytable · expires {fmtDate(data.expires_at)}
      </footer>
    </div>
  );
}
